import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import "../ReporteDocente.css";
import {
  docenteTieneMultiEtapa,
  etapasDocenteOrdenadas,
} from "../utils/etapasReporte";

const CHART_TYPE_INTERACCION = "Interacci\u00f3n en Chat";
const DOCENTE_KEY_INTERACCION = "Interacci\u00f3n plataforma";

const TXT_DESCRIPCION_INTERACCION =
  "Este gr\u00e1fico muestra el nivel de interacci\u00f3n en el chat por parte del docente, " +
  "relacionando los mensajes enviados y recibidos por su Docente de Seguimiento.";

const TITULO_INTERACCION_CHAT = "Interacci\u00f3n en Chat";

// Plugin para texto centrado
const centerTextPlugin = {
  id: "centerText",
  beforeDraw(chart) {
    const chartType = chart.config.type;

    if (chartType !== "doughnut" && chartType !== "pie") {
      return;
    }

    const { width, height } = chart;
    const ctx = chart.ctx;
    const value = chart.options.plugins?.realValue || 0;
    const title = chart.options.plugins?.title?.text;

    let displayText = `${Math.round(value * 10) / 10}%`;
    let color = "#333";

    if (chart.options.plugins?.chartType === CHART_TYPE_INTERACCION) {
      if (value < 25) {
        displayText = "Baja";
        color = "#d32f2f"; // rojo
      } else if (value < 70) {
        displayText = "Media";
        color = "#f9a825"; // amarillo
      } else {
        displayText = "Alta";
        color = "#388e3c"; // verde
      }
    } else if (title === "Progreso de Clases") {
      displayText = `${Math.round(value * 10) / 10}`;
    }

    ctx.save();
    ctx.font = "bold 20px Arial";
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(displayText, width / 2, height / 2);
    ctx.restore();
  },
};

ChartJS.register(ArcElement, Tooltip, Legend, centerTextPlugin);

const INSTITUCIONES_PROGRESO_M1_EN_M2 = new Set([
  1576, 1577, 1578, 1579, 1609, 1610, 1612, 1613, 1614, 1642,
]);

/**
 * @param {object} slice — registro global o entrada de progreso_por_etapa
 * @param {string} m — momento "1"|"2"|"3"
 */
function completadoYFuenteDesdeRegistro(slice, m) {
  const porRegistro = parseFloat(slice?.progreso_por_registro ?? 0);
  const porVisita = parseFloat(slice?.progreso_por_visita ?? 0);
  const registroRapido =
    slice?.progreso_registro_rapido != null && slice?.progreso_registro_rapido !== ""
      ? Number(slice.progreso_registro_rapido)
      : null;
  const tieneRegistroRapido =
    registroRapido != null && !Number.isNaN(registroRapido);

  let completadoReal = 0;
  if (m === "2" || m === "3") {
    completadoReal = tieneRegistroRapido
      ? Math.max(porRegistro, registroRapido)
      : porRegistro;
  } else {
    completadoReal = Math.max(porRegistro, porVisita);
  }

  const fuenteProgresoClases =
    (m === "2" || m === "3") && tieneRegistroRapido
      ? porRegistro >= registroRapido
        ? "Registro Detallado"
        : "Registro R\u00e1pido"
      : null;

  return { completadoReal, fuenteProgresoClases };
}

const GraficosSection = ({
  docente,
  interaccionData,
  progresoData,
  momento,
  institucionId,
  etapasActivas,
}) => {
  const m = String(momento ?? "1");
  const usarRangoMomento1EnMomento2 =
    m === "2" &&
    institucionId != null &&
    INSTITUCIONES_PROGRESO_M1_EN_M2.has(Number(institucionId));

  const LIMITE =
    m === "1" || usarRangoMomento1EnMomento2 ? 6 : m === "2" ? 12 : 20;

  const textoRangoIdealClases =
    m === "2" && !usarRangoMomento1EnMomento2
      ? "El rango ideal es entre 12 y 24 clases."
      : "El rango ideal es entre 8 y 12 clases.";

  let ratioText = docente[DOCENTE_KEY_INTERACCION] ?? "";

  ratioText = ratioText.toString().trim().replace(",", ".").replace(/[^0-9.]/g, "");
  let rawRatio = parseFloat(ratioText);
  if (isNaN(rawRatio)) rawRatio = 0;

  const cappedRatio = Math.max(0, Math.min(rawRatio, 1));
  const interactionValue = cappedRatio * 100;
  const complementValue = Math.max(0.0001, 100 - interactionValue);

  let interactionColor = "#d32f2f";

  if (interactionValue >= 70) {
    interactionColor = "#388e3c";
  } else if (interactionValue >= 25) {
    interactionColor = "#f9a825";
  }

  const interactionChartData = {
    labels: ["Interacci\u00f3n", "Sin Interacci\u00f3n"],
    datasets: [
      {
        data: [interactionValue, complementValue],
        backgroundColor: [interactionColor, "#E0E0E0"],
        hoverBackgroundColor: [interactionColor, "#BEBEBE"],
      },
    ],
  };

  const interactionChartOptions = {
    responsive: true,
    cutout: "80%",
    plugins: {
      legend: { display: false },
      realValue: interactionValue,
      title: {
        display: false,
        text: CHART_TYPE_INTERACCION,
      },
      chartType: CHART_TYPE_INTERACCION,
    },
  };

  const registro = progresoData.find((u) => String(u.user_id) === String(docente.user_id));

  const { completadoReal, fuenteProgresoClases, avisoFiltroSinDesglose } = useMemo(() => {
    const porEtapa = registro?.progreso_por_etapa;
    const dicta = etapasDocenteOrdenadas(docente.etapas);
    const multi = docenteTieneMultiEtapa(docente.etapas);
    const activasSet = etapasActivas instanceof Set ? etapasActivas : new Set();
    const activasOrdenadas = dicta.filter((e) => activasSet.has(e));
    const todasActivas =
      dicta.length > 0 &&
      activasOrdenadas.length === dicta.length &&
      dicta.every((e) => activasSet.has(e));

    const tieneDesglose =
      porEtapa &&
      typeof porEtapa === "object" &&
      Object.keys(porEtapa).length > 0;

    if (!multi || !tieneDesglose || todasActivas) {
      const base = completadoYFuenteDesdeRegistro(registro, m);
      return {
        completadoReal: base.completadoReal,
        fuenteProgresoClases: base.fuenteProgresoClases,
        avisoFiltroSinDesglose:
          multi && !todasActivas && !tieneDesglose
            ? "No hay desglose por etapa en los datos; se muestra el avance total."
            : null,
      };
    }

    const valores = [];
    const fuentes = [];
    for (const e of activasOrdenadas) {
      const slice = porEtapa[e];
      if (!slice) continue;
      const part = completadoYFuenteDesdeRegistro(slice, m);
      valores.push(part.completadoReal);
      if (part.fuenteProgresoClases) fuentes.push(part.fuenteProgresoClases);
    }

    if (!valores.length) {
      const base = completadoYFuenteDesdeRegistro(registro, m);
      return {
        completadoReal: base.completadoReal,
        fuenteProgresoClases: base.fuenteProgresoClases,
        avisoFiltroSinDesglose: "Sin datos de avance para las etapas seleccionadas; se muestra el total.",
      };
    }

    const prom = valores.reduce((a, b) => a + b, 0) / valores.length;
    const uniq = [...new Set(fuentes)];
    const fuente =
      uniq.length === 1 ? uniq[0] : uniq.length > 1 ? "Combinado" : null;

    return {
      completadoReal: prom,
      fuenteProgresoClases: fuente,
      avisoFiltroSinDesglose: null,
    };
  }, [registro, docente.etapas, etapasActivas, m]);

  const completadoGrafico = Math.min(completadoReal, LIMITE);
  const pendienteGrafico = LIMITE - completadoGrafico;

  const progresoChartData = {
    labels: ["Completado", "Por Completar"],
    datasets: [
      {
        data: [completadoGrafico, pendienteGrafico],
        backgroundColor: ["#330072", "#E0E0E0"],
        hoverBackgroundColor: ["#330072", "#BEBEBE"],
      },
    ],
  };

  const progresoChartOptions = {
    responsive: true,
    cutout: "80%",
    plugins: {
      legend: { display: false },
      realValue: completadoReal,
      title: {
        display: false,
        text: "Progreso de Clases",
      },
      chartType: "Progreso de Clases",
    },
  };

  const textoDescripcionProgreso =
    "Este gr\u00e1fico refleja un promedio referencial de avance en las clases del docente, " +
    "con base en la plataforma Kurios y las visitas de acompa\u00f1amiento. " +
    textoRangoIdealClases;

  return (
    <div className="graficos-section">
      <div className="grafico-item">
        <h4>{TITULO_INTERACCION_CHAT}</h4>
        <Doughnut data={interactionChartData} options={interactionChartOptions} />
        <p className="grafico-descripcion">{TXT_DESCRIPCION_INTERACCION}</p>
      </div>

      <div className="grafico-item">
        <h4>Avance Promedio de Clases</h4>
        {fuenteProgresoClases && <p className="grafico-progreso-fuente">{fuenteProgresoClases}</p>}
        <Doughnut data={progresoChartData} options={progresoChartOptions} />
        {avisoFiltroSinDesglose && (
          <p className="grafico-progreso-aviso">{avisoFiltroSinDesglose}</p>
        )}
        <p className="grafico-descripcion">{textoDescripcionProgreso}</p>
      </div>
    </div>
  );
};

export default GraficosSection;
